<template>
  <div class="pwa-stack" aria-live="polite">
    <div v-if="!isOnline" class="pwa-card pwa-card--offline" role="status">
      <VaIcon name="cloud_off" size="1.1rem" />
      <span class="pwa-card__text">{{ t('pwa.offline') }}</span>
    </div>

    <div v-if="needRefresh" class="pwa-card" role="alert">
      <VaIcon name="system_update" size="1.1rem" />
      <span class="pwa-card__text">{{ t('pwa.update_available') }}</span>
      <VaButton size="small" preset="secondary" color="#ffffff" @click="needRefresh = false">
        {{ t('pwa.later') }}
      </VaButton>
      <VaButton size="small" color="primary" @click="updateServiceWorker(true)">
        {{ t('pwa.update') }}
      </VaButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useOnline } from '@vueuse/core'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { addToast } from '@/services/toastService'

const { t } = useI18n()
const isOnline = useOnline()

// O HUB fica aberto o dia todo numa aba; sem checar de tempos em tempos, quem
// não recarrega nunca vê o deploy novo.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

const { needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl, registration) {
    if (!registration) return
    setInterval(async () => {
      if (registration.installing || !navigator.onLine) return
      // Sem isso, com o servidor fora o update() falha em silêncio a cada hora.
      const resp = await fetch(swUrl, { cache: 'no-store', headers: { 'cache-control': 'no-cache' } }).catch(() => null)
      if (resp?.status === 200) await registration.update()
    }, UPDATE_CHECK_INTERVAL_MS)
  },
  onOfflineReady() {
    addToast(t('pwa.offline_ready'), 'success')
  },
})
</script>

<style scoped>
.pwa-stack {
  position: fixed;
  left: 50%;
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  max-width: calc(100vw - 2rem);
  pointer-events: none;
}

.pwa-card {
  pointer-events: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding: 0.6rem 0.9rem;
  border-radius: 0.75rem;
  background: #16161a;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  font-size: 0.875rem;
}

.pwa-card--offline {
  background: #3a2a00;
  border-color: rgba(255, 196, 0, 0.35);
}

.pwa-card__text {
  flex: 1 1 auto;
}
</style>
